import * as React from "react";
import { motion, Variants } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import { cn } from "@babackapp/customer-ui/lib/utils";
import { AnimatedList, AnimatedListItem } from "./animated-list";

const buttonVariants = cva(
  "group/button font-content inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground [a]:hover:bg-primary/80 shadow-xl active:shadow-md active:scale-[0.99] transition-all",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30 dark:focus-visible:ring-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export function AnimatedButtonRow({
  className,
  ...props
}: React.ComponentProps<typeof AnimatedList>) {
  return (
    <AnimatedList
      className={cn(
        "w-full justify-center items-center flex flex-row gap-2 flex-wrap mt-4",
        className,
      )}
      {...props}
    />
  );
}

const animatedButtonVariants: Variants = {
  enter: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      // delay: 0.3,
      ease: [0.16, 1, 0.3, 1], // expo out
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.4,
      ease: [0.7, 0, 0.84, 0], // expo in
    },
  },
};

export function AnimatedButtonContainer({
  className,
  ...props
}: React.ComponentProps<typeof AnimatedListItem>) {
  return (
    <AnimatedListItem
      variants={animatedButtonVariants}
      layout="preserve-aspect"
      className={cn("grow-0", className)}
      {...props}
    />
  );
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export function LabeledButtonLabel({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="button-inside-label"
      className={cn(
        "text-muted-foreground font-medium font-content flex items-center gap-2 text-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 col-start-1 row-start-1",
        className,
      )}
      {...props}
    />
  );
}

export function LabeledButtonContent({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="button-inside-label"
      className={cn("col-start-1 row-start-2", className)}
      {...props}
    />
  );
}

export function LabeledButton({
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      data-slot="labeled-button"
      variant="outline"
      className={cn(
        "h-auto w-full grid grid-cols-[1fr_auto] [&>svg]:col-start-2 [&>svg]:row-start-1 [&>svg]:row-span-2 text-left gap-0 py-1",
        className,
      )}
      {...props}
    />
  );
}

export { Button, buttonVariants };
